/** Angular */
import { Component, OnInit, Input, Output, EventEmitter, Inject, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DOCUMENT } from '@angular/common';
/** Sixense */
import { DsPasswordService } from '@sixense/ds-roazhon-service';
import { BydAuthService, BydAuthSsoService } from '@sixense/byd-auth-service';
/** Enums */
import { DsLibVinciUsersEnums } from './enums/ds-lib-vinci-users.enums';
/** Models */
import { DsLogos } from './models/ds-logos';
/** Constants */
import { BYD_SSO_DOMAINS } from './constants/byd-sso-domains.const';

/** In order to resolve type Document in Aot */
/** @dynamic */
@Component({
  selector: 'ds-lib-login-form',
  templateUrl: 'ds-lib-login-form.component.html',
  styleUrls: ['ds-lib-login-form.component.scss'],
})
/**
 * background can be added using css from the parent component.
 * Ex: background: url("photo.jpg") no-repeat fixed right;
 * background-size: cover;
 */
export class DsLibLoginFormComponent implements OnInit {
  /**
   * The steeper view child
   */
  @ViewChild('stepper') private stepper: MatStepper;

  /**
   * The login form
   */
  public loginForm: FormGroup;

  /**
   * The authentication error message
   */
  public authErrSubLabel = '';

  /**
   * The spinner status
   */
  public showSpinner: boolean;

  /**
   * The reset mode
   */
  public resetmode = false;

  /**
   * The user token
   */
  public token: string;

  /**
   * The reset url
   */
  @Input() resetUrl: string;

  /**
   * Product logo path
   */
  @Input() logos: DsLogos;

  /**
   * Name of the product. Will be displayed for the copyright
   */
  @Input() productName: string;

  /**
   * An EventEmitter fired when the user is authenticated
   */
  @Output() loggedIn = new EventEmitter();

  /**
   * Double-way binding for language.
   */
  @Input() language: string;

  /**
   * An EventEmitter fired when the language is changed
   */
  @Output() languageChange = new EventEmitter<string>();

  /**
   * The login sso domain status.
   */
  public ssoDomain: boolean;

  /**
   * Creates an instance of DsLibLoginFormComponent.
   *
   * @param fb creating instances of a FormControl, FormGroup, or FormArray
   * @param route contains the information about a route associated
   * @param translate TranslateService
   * @param dsPasswordService DsPasswordService
   * @param snackBar service to dispatch Material Design snack bar messages
   * @param bydAuthService the byd authentication service
   * @param bydAuthSsoService the byd sso authentication service
   * @param document the DOM tree
   */
  constructor(
    private fb: FormBuilder,
    private bydAuthService: BydAuthService,
    private bydAuthSsoService: BydAuthSsoService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private dsPasswordService: DsPasswordService,
    public snackBar: MatSnackBar,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Create the loginForm.
   */
  ngOnInit() {
    this.loginForm = this.fb.group({
      login: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required],
    });

    this.route.queryParams.subscribe((params) => {
      if (params?.reset_token) {
        this.token = params.reset_token;
        this.switchResetMode();
      }
    });
    this.updateDomain();
  }

  /**
   * Get the login control.
   *
   * @returns AbstractControl
   */
  get loginControl() {
    return this.loginForm.get('login');
  }

  /**
   * Get the password control.
   *
   * @returns AbstractControl
   */
  get passwordControl() {
    return this.loginForm.get('password');
  }

  /**
   * Switch the button spinner.
   */
  switchSpinner() {
    this.showSpinner = !this.showSpinner;
  }

  /**
   * Authenticate user.
   */
  submitForm() {
    this.switchSpinner();
    this.bydAuthService
      .login(this.loginForm.value.login, this.loginForm.value.password)
      .pipe(first())
      .subscribe({
        next: (authOk) => {
          if (authOk) {
            this.loggedIn.emit();
          } else {
            this.authErrSubLabel = this.translate.instant('dsLibCommon.login.invalidCredentials');
          }
        },
        error: () => {
          this.switchSpinner();
        },
        complete: () => {
          this.switchSpinner();
        },
      });
  }

  /**
   * Redirect the user to azure sso login.
   */
  loginSso(): void {
    this.bydAuthSsoService
      .login(this.loginForm.value.login)
      .pipe(first())
      .subscribe((result) => {
        this.document.location.href = result.redirectUri;
      });
  }

  /**
   * Emit the language change.
   *
   * @param lang the changed language
   */
  onLanguageChange(lang: string) {
    this.languageChange.emit(lang);
  }

  /**
   * Switch the reset mode true/false.
   */
  switchResetMode() {
    this.resetmode = !this.resetmode;
  }

  /**
   * Check vinci domains.
   *
   * @param login the user login
   * @returns the value of the login domain
   */
  checkVinciDomains(login: string) {
    const vinciUsersDomain = [DsLibVinciUsersEnums.VINCI_COM, DsLibVinciUsersEnums.VINCI_FR];
    return vinciUsersDomain.find((domain) => login.endsWith(domain));
  }

  /**
   * Send the reset password email.
   */
  sendResetEmail() {
    if (this.loginControl.errors && this.checkVinciDomains(this.loginControl.value)) {
      this.snackBar.open(this.translate.instant('dsLibCommon.login.validLoginDomain'), null, {
        panelClass: ['mat-warn'],
        duration: 5000,
      });
    } else {
      this.dsPasswordService
        .sendResetEmail(this.loginForm.value.login, this.language, this.document.location.href.split('?')[0])
        .subscribe((result: boolean) => {
          if (result) {
            this.snackBar.open(this.translate.instant('dsLibCommon.login.resetEmail'), null, {
              panelClass: ['mat-primary'],
              duration: 5000,
            });
          }
        });
    }
  }

  /**
   * Next login step.
   */
  nextStep(): void {
    if (this.ssoDomain && this.selectedStep(0)) {
      this.loginSso();
      return;
    }
    if (!this.ssoDomain && this.selectedStep(1)) {
      this.submitForm();
      return;
    }
    this.stepper.next();
  }

  /**
   * Check step selection.
   *
   * @param index selected step index
   * @returns boolean
   */
  public selectedStep(index: number): boolean {
    return this.stepper?.selectedIndex === index;
  }

  /**
   * Update the user sso domain status.
   */
  public updateDomain(): void {
    this.loginControl.valueChanges.subscribe((value) => {
      this.ssoDomain = this.checkSsoDomains(value);
    });
  }

  /**
   * Check login sso domains.
   *
   * @param login the user login
   * @returns boolean
   */
  private checkSsoDomains(login: string): boolean {
    return BYD_SSO_DOMAINS.some((domain) => login.endsWith(domain));
  }
}
